<div class="sales-funnel">
    <!-- Header -->
    <div class="sales-funnel__header">
        <div class="sales-funnel__header-content">
            <div class="sales-funnel__title-row">
                <h2 class="sales-funnel__title">{{ title }}</h2>
                <button
                    class="sales-funnel__info"
                    [eiTooltip]="salesFunnelTooltip"
                    eiTooltipPosition="top"
                    aria-label="Info"
                    type="button"
                >
                    <img
                        src="assets/images/icons/info-icon.svg"
                        alt=""
                        class="sales-funnel__info-icon"
                    />
                </button>
            </div>
        </div>
        <div class="sales-funnel__view-toggle">
            <button
                class="view-toggle__btn"
                [class.view-toggle__btn--active]="isActiveView(ESalesFunnelView.FUNNEL)"
                (click)="switchView(ESalesFunnelView.FUNNEL)"
                type="button"
            >
                Funnel
            </button>
            <button
                class="view-toggle__btn"
                [class.view-toggle__btn--active]="isActiveView(ESalesFunnelView.DATA)"
                (click)="switchView(ESalesFunnelView.DATA)"
                type="button"
            >
                <svg
                    width="16"
                    height="16"
                    viewBox="0 0 16 16"
                    fill="none"
                    xmlns="http://www.w3.org/2000/svg"
                    class="view-toggle__icon"
                >
                    <path
                        d="M2 3.5h12M2 8h12M2 12.5h12"
                        stroke="currentColor"
                        stroke-width="1.5"
                        stroke-linecap="round"
                    />
                </svg>
                Data
            </button>
        </div>
    </div>

    <!-- Data View (Table) -->
    @if (currentView() === ESalesFunnelView.DATA) {
        <div class="sales-funnel__content">
            @if (hasError()) {
                <div class="sales-funnel__error">
                    <p class="sales-funnel__error-message">{{ errorMessage() }}</p>
                </div>
            } @else if (isLoading()) {
                <div class="sales-funnel__skeleton">
                    <div class="data-table">
                        <div class="data-table__header">
                            @for (header of dataTableHeaderSkeletonItems; track header) {
                                <div class="data-table__cell">
                                    <div class="skeleton skeleton-label"></div>
                                </div>
                            }
                        </div>
                        <div class="data-table__body">
                            @for (row of dataTableRowSkeletonItems; track row) {
                                <div class="data-table__row">
                                    @for (cell of dataTableHeaderSkeletonItems; track cell) {
                                        <div class="data-table__cell">
                                            <div class="skeleton skeleton-value"></div>
                                        </div>
                                    }
                                </div>
                            }
                        </div>
                    </div>
                </div>
            } @else {
                <div class="data-table">
                    <div class="data-table__header">
                        <div class="data-table__cell data-table__cell--stage">Stage</div>
                        <div class="data-table__cell data-table__cell--code">Code</div>
                        <div
                            class="data-table__cell data-table__cell--count data-table__cell--numeric"
                        >
                            Count of Schools
                        </div>
                        <div
                            class="data-table__cell data-table__cell--revenue data-table__cell--numeric"
                        >
                            Potential Revenue
                        </div>
                        <div
                            class="data-table__cell data-table__cell--conversion data-table__cell--numeric"
                        >
                            Conversion Rate by Stage
                        </div>
                        <div
                            class="data-table__cell data-table__cell--droprate data-table__cell--numeric"
                        >
                            Drop Rate by Stage
                        </div>
                    </div>

                    <div class="data-table__body">
                        @for (stage of stages(); track stage.code) {
                            <div class="data-table__row">
                                <div class="data-table__cell data-table__cell--stage">
                                    <span class="stage-name">{{ stage.name }}</span>
                                </div>
                                <div class="data-table__cell data-table__cell--code">
                                    <span class="stage-code">{{ stage.displayCode }}</span>
                                </div>
                                <div
                                    class="data-table__cell data-table__cell--count data-table__cell--numeric"
                                >
                                    <span class="stage-count">{{ stage.count }}</span>
                                </div>
                                <div
                                    class="data-table__cell data-table__cell--revenue data-table__cell--numeric"
                                >
                                    @if (shouldShowRevenue(stage)) {
                                        <span class="stage-revenue">{{ stage.revenue }}</span>
                                    } @else {
                                        <span class="stage-revenue--empty">{{
                                            valueConstants.NA
                                        }}</span>
                                    }
                                </div>
                                <div
                                    class="data-table__cell data-table__cell--conversion data-table__cell--numeric"
                                >
                                    @if (shouldShowConversion(stage)) {
                                        <span class="conversion-badge">{{ stage.conversion }}</span>
                                    }
                                </div>
                                <div
                                    class="data-table__cell data-table__cell--droprate data-table__cell--numeric"
                                >
                                    @if (stage.dropRate && stage.dropRate !== valueConstants.NA) {
                                        <span class="droprate-badge">{{ stage.dropRate }}</span>
                                    } @else {
                                        <span class="stage-revenue--empty">{{
                                            valueConstants.NA
                                        }}</span>
                                    }
                                </div>
                            </div>
                        }
                    </div>
                </div>

                <!-- Stage Leakage Section -->
                <div class="stage-leakage">
                    <div class="stage-leakage__header">
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            class="stage-leakage__icon"
                        >
                            <path
                                d="M8.57 1.5l6.93 12a.5.5 0 01-.43.75H2.93a.5.5 0 01-.43-.75l6.93-12a.5.5 0 01.86 0z"
                                stroke="#dc2626"
                                stroke-width="1.5"
                                fill="none"
                            />
                            <path
                                d="M9 6v3.5M9 11.5v.5"
                                stroke="#dc2626"
                                stroke-width="1.5"
                                stroke-linecap="round"
                            />
                        </svg>
                        <h3>Stage Leakage (Sales Lost)</h3>
                    </div>
                    @if (hasError()) {
                        <div class="stage-leakage__error">
                            <p class="stage-leakage__error-message">
                                Failed to load stage leakage data
                            </p>
                        </div>
                    } @else {
                        <div class="stage-leakage__grid">
                            @if (isLoading()) {
                                @for (skeleton of leakageCardSkeletonItems; track skeleton) {
                                    <div class="leakage-card">
                                        <div class="leakage-card__badge">
                                            <div class="skeleton skeleton-value"></div>
                                        </div>
                                        <div class="leakage-card__content">
                                            <div class="skeleton skeleton-label"></div>
                                            <div class="skeleton skeleton-label"></div>
                                        </div>
                                    </div>
                                }
                            } @else {
                                @for (
                                    leakage of stageLeakages();
                                    track leakage.fromStage + leakage.toStage
                                ) {
                                    <div class="leakage-card">
                                        <div class="leakage-card__badge">
                                            <span class="leakage-card__count">{{
                                                leakage.count
                                            }}</span>
                                        </div>
                                        <div class="leakage-card__content">
                                            <div class="leakage-card__stages">
                                                {{ leakage.fromStage }} → {{ leakage.toStage }}
                                            </div>
                                            <div class="leakage-card__revenue">
                                                {{ leakage.revenueLost }}
                                            </div>
                                        </div>
                                    </div>
                                }
                            }
                        </div>
                    }
                </div>
            }
        </div>
    }

    <!-- Funnel View (Visual Progress Bars) -->
    @if (currentView() === ESalesFunnelView.FUNNEL) {
        <div class="sales-funnel__content">
            @if (hasError()) {
                <div class="sales-funnel__error">
                    <p class="sales-funnel__error-message">{{ errorMessage() }}</p>
                </div>
            } @else if (isLoading()) {
                <div class="sales-funnel__skeleton">
                    <div class="stages-list">
                        @for (skeleton of dataTableRowSkeletonItems; track skeleton) {
                            <div class="stage-item">
                                <div class="stage-item__header">
                                    <div class="skeleton skeleton-label"></div>
                                    <div class="skeleton skeleton-value"></div>
                                </div>
                                <div class="stage-item__progress">
                                    <div class="skeleton skeleton-value"></div>
                                </div>
                            </div>
                        }
                    </div>
                </div>
            } @else {
                <!-- Stages List -->
                <div class="stages-list">
                    @for (stage of stages(); track stage.code) {
                        <div class="stage-item">
                            <div class="stage-item__header">
                                <div class="stage-item__info">
                                    <span class="stage-item__code">{{ stage.displayCode }}</span>
                                    <span class="stage-item__separator">-</span>
                                    <span class="stage-item__name">{{ stage.name }}</span>
                                    <span class="stage-item__count">{{ stage.count }}</span>
                                </div>
                                <div class="stage-item__stats">
                                    @if (shouldShowRevenue(stage)) {
                                        <span class="stage-item__revenue">{{ stage.revenue }}</span>
                                    }
                                </div>
                            </div>
                            <div class="stage-item__progress">
                                <div
                                    class="stage-item__progress-bar"
                                    [style.width.%]="stage.progressWidth"
                                    [style.background-color]="stage.progressColor"
                                ></div>
                            </div>
                            @if (shouldShowConversion(stage)) {
                                <div class="stage-item__conversion">
                                    <span class="stage-item__arrow" aria-hidden="true"></span>
                                    <span
                                        >Conversion: <strong>{{ stage.conversion }}</strong></span
                                    >
                                </div>
                            }
                        </div>
                    }
                </div>

                <!-- Stage Leakage Section -->
                <div class="stage-leakage">
                    <div class="stage-leakage__header">
                        <svg
                            width="18"
                            height="18"
                            viewBox="0 0 18 18"
                            fill="none"
                            xmlns="http://www.w3.org/2000/svg"
                            class="stage-leakage__icon"
                        >
                            <path
                                d="M8.57 1.5l6.93 12a.5.5 0 01-.43.75H2.93a.5.5 0 01-.43-.75l6.93-12a.5.5 0 01.86 0z"
                                stroke="#dc2626"
                                stroke-width="1.5"
                                fill="none"
                            />
                            <path
                                d="M9 6v3.5M9 11.5v.5"
                                stroke="#dc2626"
                                stroke-width="1.5"
                                stroke-linecap="round"
                            />
                        </svg>
                        <h3>Stage Leakage (Sales Lost)</h3>
                    </div>
                    @if (hasError()) {
                        <div class="stage-leakage__error">
                            <p class="stage-leakage__error-message">
                                Failed to load stage leakage data
                            </p>
                        </div>
                    } @else {
                        <div class="stage-leakage__grid">
                            @if (isLoading()) {
                                @for (skeleton of leakageCardSkeletonItems; track skeleton) {
                                    <div class="leakage-card">
                                        <div class="leakage-card__badge">
                                            <div class="skeleton skeleton-value"></div>
                                        </div>
                                        <div class="leakage-card__content">
                                            <div class="skeleton skeleton-label"></div>
                                            <div class="skeleton skeleton-label"></div>
                                        </div>
                                    </div>
                                }
                            } @else {
                                @for (
                                    leakage of stageLeakages();
                                    track leakage.fromStage + leakage.toStage
                                ) {
                                    <div class="leakage-card">
                                        <div class="leakage-card__badge">
                                            <span class="leakage-card__count">{{
                                                leakage.count
                                            }}</span>
                                        </div>
                                        <div class="leakage-card__content">
                                            <div class="leakage-card__stages">
                                                {{ leakage.fromStage }} → {{ leakage.toStage }}
                                            </div>
                                            <div class="leakage-card__revenue">
                                                {{ leakage.revenueLost }}
                                            </div>
                                        </div>
                                    </div>
                                }
                            }
                        </div>
                    }
                </div>
            }
        </div>
    }
</div>
